import { create } from "zustand";
import { temporal } from "zundo";
import { shallow } from "zustand/shallow";

export type PageType =
  | "landing"
  | "dashboard"
  | "settings"
  | "onboarding"
  | "ecommerce"
  | "blog"
  | "docs"
  | "portfolio";

export type LayoutType =
  | "single-column"
  | "sidebar-left"
  | "sidebar-right"
  | "grid"
  | "full-width-sections";

export type StyleType =
  | "minimal"
  | "bold"
  | "soft"
  | "glass"
  | "elegant"
  | "corporate";

export type ThemeType = "light" | "dark" | "both";

export type AccentColor =
  | "blue"
  | "purple"
  | "green"
  | "orange"
  | "red"
  | "pink"
  | "neutral";

export type TypographyType =
  | "space-grotesk"
  | "bricolage-grotesque"
  | "ibm-plex-sans"
  | "source-sans"
  | "playfair"
  | "crimson-pro"
  | "newsreader"
  | "jetbrains-mono"
  | "fira-code"
  | "ibm-plex-mono";

export type SpacingType = "compact" | "comfortable" | "spacious";

export type AnimationType = "none" | "subtle" | "rich";

export type CornerStyleType = "none" | "small" | "medium" | "large";

export type ShadowType = "none" | "subtle" | "medium" | "strong";

export interface StylesBuilderSelections {
  pageType: PageType | null;
  layout: LayoutType | null;
  style: StyleType | null;
  theme: ThemeType | null;
  accentColor: AccentColor | null;
  typography: TypographyType | null;
  spacing: SpacingType | null;
  animation: AnimationType | null;
  cornerStyle: CornerStyleType | null;
  shadow: ShadowType | null;
}

export interface Preset {
  id: string;
  name: string;
  selections: Partial<StylesBuilderSelections>;
}

export const PRESETS: Preset[] = [
  {
    id: "brutalist",
    name: "Brutalist",
    selections: {
      pageType: "docs",
      style: "minimal",
      theme: "dark",
      accentColor: "red",
      typography: "ibm-plex-mono",
      spacing: "compact",
      animation: "none",
      cornerStyle: "none",
      shadow: "none",
    },
  },
  {
    id: "luxe",
    name: "Midnight Luxe",
    selections: {
      pageType: "landing",
      style: "elegant",
      theme: "dark",
      accentColor: "neutral",
      typography: "playfair",
      spacing: "spacious",
      animation: "subtle",
      cornerStyle: "small",
      shadow: "subtle",
    },
  },
  {
    id: "sunrise",
    name: "Sunrise",
    selections: {
      pageType: "blog",
      style: "soft",
      theme: "light",
      accentColor: "orange",
      typography: "newsreader",
      spacing: "spacious",
      animation: "subtle",
      cornerStyle: "medium",
      shadow: "medium",
    },
  },
  {
    id: "terminal",
    name: "Terminal",
    selections: {
      pageType: "dashboard",
      style: "corporate",
      theme: "dark",
      accentColor: "green",
      typography: "jetbrains-mono",
      spacing: "compact",
      animation: "none",
      cornerStyle: "small",
      shadow: "none",
    },
  },
  {
    id: "neon",
    name: "Neon",
    selections: {
      pageType: "ecommerce",
      style: "bold",
      theme: "dark",
      accentColor: "pink",
      typography: "space-grotesk",
      spacing: "comfortable",
      animation: "rich",
      cornerStyle: "medium",
      shadow: "strong",
    },
  },
  {
    id: "aurora",
    name: "Aurora",
    selections: {
      pageType: "settings",
      style: "glass",
      theme: "dark",
      accentColor: "blue",
      typography: "bricolage-grotesque",
      spacing: "comfortable",
      animation: "subtle",
      cornerStyle: "large",
      shadow: "medium",
    },
  },
];

export const PAGE_TYPE_OPTIONS: { value: PageType; label: string }[] = [
  { value: "landing", label: "Landing Page" },
  { value: "dashboard", label: "Dashboard" },
  { value: "settings", label: "Settings" },
  { value: "onboarding", label: "Onboarding" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "blog", label: "Blog" },
  { value: "docs", label: "Documentation" },
  { value: "portfolio", label: "Portfolio" },
];

export const LAYOUT_OPTIONS: { value: LayoutType; label: string }[] = [
  { value: "single-column", label: "Single Column" },
  { value: "sidebar-left", label: "Sidebar Left" },
  { value: "sidebar-right", label: "Sidebar Right" },
  { value: "grid", label: "Grid" },
  { value: "full-width-sections", label: "Full Width" },
];

export const STYLE_OPTIONS: { value: StyleType; label: string }[] = [
  { value: "minimal", label: "Minimal" },
  { value: "bold", label: "Bold" },
  { value: "soft", label: "Soft" },
  { value: "glass", label: "Glass" },
  { value: "elegant", label: "Elegant" },
  { value: "corporate", label: "Corporate" },
];

export const THEME_OPTIONS: { value: ThemeType; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "both", label: "Both" },
];

export const ACCENT_COLOR_OPTIONS: {
  value: AccentColor;
  label: string;
  hex: string;
}[] = [
  { value: "orange", label: "Ember", hex: "#F97316" },
  { value: "red", label: "Vermillion", hex: "#E63946" },
  { value: "pink", label: "Magenta", hex: "#FF2D92" },
  { value: "green", label: "Teal", hex: "#0D9488" },
  { value: "purple", label: "Violet", hex: "#7C3AED" },
  { value: "blue", label: "Cyan", hex: "#06B6D4" },
  { value: "neutral", label: "Gold", hex: "#D4A853" },
];

export type FontCategory = "sans" | "serif" | "mono";

export const TYPOGRAPHY_OPTIONS: {
  value: TypographyType;
  label: string;
  fontFamily: string;
  category: FontCategory;
}[] = [
  {
    value: "space-grotesk",
    label: "Space Grotesk",
    fontFamily: "'Space Grotesk', sans-serif",
    category: "sans",
  },
  {
    value: "bricolage-grotesque",
    label: "Bricolage Grotesque",
    fontFamily: "'Bricolage Grotesque', sans-serif",
    category: "sans",
  },
  {
    value: "ibm-plex-sans",
    label: "IBM Plex Sans",
    fontFamily: "'IBM Plex Sans', sans-serif",
    category: "sans",
  },
  {
    value: "source-sans",
    label: "Source Sans 3",
    fontFamily: "'Source Sans 3', sans-serif",
    category: "sans",
  },
  {
    value: "playfair",
    label: "Playfair Display",
    fontFamily: "'Playfair Display', serif",
    category: "serif",
  },
  {
    value: "crimson-pro",
    label: "Crimson Pro",
    fontFamily: "'Crimson Pro', serif",
    category: "serif",
  },
  {
    value: "newsreader",
    label: "Newsreader",
    fontFamily: "'Newsreader', serif",
    category: "serif",
  },
  {
    value: "jetbrains-mono",
    label: "JetBrains Mono",
    fontFamily: "'JetBrains Mono', monospace",
    category: "mono",
  },
  {
    value: "fira-code",
    label: "Fira Code",
    fontFamily: "'Fira Code', monospace",
    category: "mono",
  },
  {
    value: "ibm-plex-mono",
    label: "IBM Plex Mono",
    fontFamily: "'IBM Plex Mono', monospace",
    category: "mono",
  },
];

export const SPACING_OPTIONS: { value: SpacingType; label: string }[] = [
  { value: "compact", label: "Compact" },
  { value: "comfortable", label: "Comfortable" },
  { value: "spacious", label: "Spacious" },
];

export const ANIMATION_OPTIONS: { value: AnimationType; label: string }[] = [
  { value: "none", label: "None" },
  { value: "subtle", label: "Subtle" },
  { value: "rich", label: "Rich" },
];

export const CORNER_STYLE_OPTIONS: { value: CornerStyleType; label: string }[] =
  [
    { value: "none", label: "Sharp (0px)" },
    { value: "small", label: "Small (4px)" },
    { value: "medium", label: "Medium (8px)" },
    { value: "large", label: "Large (12px)" },
  ];

export const SHADOW_OPTIONS: { value: ShadowType; label: string }[] = [
  { value: "none", label: "None" },
  { value: "subtle", label: "Subtle" },
  { value: "medium", label: "Medium" },
  { value: "strong", label: "Strong" },
];

const DEFAULT_SELECTIONS: StylesBuilderSelections = {
  pageType: null,
  layout: null,
  style: "elegant",
  theme: "dark",
  accentColor: "neutral",
  typography: "playfair",
  spacing: "spacious",
  animation: "subtle",
  cornerStyle: "small",
  shadow: "subtle",
};

interface StylesBuilderStore {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;

  selections: StylesBuilderSelections;
  setSelection: <K extends keyof StylesBuilderSelections>(
    key: K,
    value: StylesBuilderSelections[K],
  ) => void;

  activePresetId: string | null;
  applyPreset: (presetId: string) => void;

  previewPresetId: string | null;
  setPreviewPreset: (presetId: string | null) => void;
  getEffectiveSelections: () => StylesBuilderSelections;

  randomizeAll: () => void;
  reset: () => void;
  hasSelections: () => boolean;
}

export const useStylesBuilderStore = create<StylesBuilderStore>()(
  temporal(
    (set, get) => ({
      isOpen: false,
      openModal: () => set({ isOpen: true }),
      closeModal: () => set({ isOpen: false }),

      selections: { ...DEFAULT_SELECTIONS },
      setSelection: (key, value) =>
        set((state) => ({
          selections: { ...state.selections, [key]: value },
          activePresetId: null,
        })),

      activePresetId: "luxe",
      applyPreset: (presetId) => {
        const preset = PRESETS.find((p) => p.id === presetId);
        if (!preset) return;

        set({
          selections: { ...DEFAULT_SELECTIONS, ...preset.selections },
          activePresetId: presetId,
          previewPresetId: null,
        });
      },

      previewPresetId: null,
      setPreviewPreset: (presetId) => set({ previewPresetId: presetId }),
      getEffectiveSelections: () => {
        const { selections, previewPresetId } = get();
        if (previewPresetId) {
          const preset = PRESETS.find((p) => p.id === previewPresetId);
          if (preset) {
            return { ...DEFAULT_SELECTIONS, ...preset.selections };
          }
        }
        return selections;
      },

      randomizeAll: () => {
        const randomChoice = <T>(arr: readonly { value: T }[]): T =>
          arr[Math.floor(Math.random() * arr.length)].value;

        set({
          selections: {
            pageType: randomChoice(PAGE_TYPE_OPTIONS),
            layout: randomChoice(LAYOUT_OPTIONS),
            style: randomChoice(STYLE_OPTIONS),
            theme: randomChoice(THEME_OPTIONS),
            accentColor: randomChoice(ACCENT_COLOR_OPTIONS),
            typography: randomChoice(TYPOGRAPHY_OPTIONS),
            spacing: randomChoice(SPACING_OPTIONS),
            animation: randomChoice(ANIMATION_OPTIONS),
            cornerStyle: randomChoice(CORNER_STYLE_OPTIONS),
            shadow: randomChoice(SHADOW_OPTIONS),
          },
          activePresetId: null,
        });
      },

      reset: () =>
        set({
          selections: { ...DEFAULT_SELECTIONS },
          activePresetId: "luxe",
        }),

      hasSelections: () => {
        const { selections } = get();
        return Object.values(selections).some((v) => v !== null);
      },
    }),
    {
      partialize: (state) => ({ selections: state.selections }),
      equality: (pastState, currentState) =>
        shallow(pastState.selections, currentState.selections),
      limit: 50,
    },
  ),
);
